import { useCallback, useState } from "react";
import { useStored } from "~/core/hooks";
import { useI18n } from "~/core/i18n";
import type { FilterState } from "~/modules/analytics/components/filters";

/**
 * The filter bar's state.
 *
 * The three segments every screen reads — the window, the engine and the
 * content language — are remembered per browser. The four the insight readings
 * add live for the session only, and two of them belong to one brand: a topic
 * and a citation tag are names that exist in this brand's data and no other's,
 * so a change of brand clears them. The prompt type and the citation category
 * name things every brand has, and survive the switch.
 */
export function useFilters(
	entityId: string,
	/**
	 * The languages this brand has questions in. The same argument as `engines`
	 * on the filter bar: a language this brand was never asked in is a language
	 * whose only effect is to empty the screen — and worse, to put "you are not
	 * asking anything" on the overview of a brand that is being measured every
	 * week in the other one.
	 */
	available?: readonly string[],
): [FilterState, (next: Partial<FilterState>) => void] {
	const [range, setRange] = useStored<string>("ondit.range", "30d");
	const [engine, setEngine] = useStored<string>("ondit.engine", "");
	// The account's own language, not English: the onboarding language step
	// decides what language the questions are written in, and a Chinese account
	// opening an English reading sees an empty brand on its first day.
	const { locale: ui } = useI18n();
	const [locale, setLocale] = useStored<string>("ondit.contentLocale", ui === "zh" ? "zh" : "en");
	const [scoped, setScoped] = useState<Scoped>({
		entityId,
		topics: "",
		intent: "",
		category: "",
		tag: "",
		locale: "",
	});

	// Reset while rendering rather than in an effect: an effect lets one render
	// go out with the old brand's topics against the new brand's numbers.
	const current = scoped.entityId === entityId ? scoped : rescope(scoped, entityId);
	if (current !== scoped) setScoped(current);

	const set = useCallback(
		(next: Partial<FilterState>) => {
			if (next.range !== undefined) setRange(next.range);
			if (next.engine !== undefined) setEngine(next.engine);
			// Both: the person's default for the next brand, and their choice on
			// this one — which has to survive `available` not containing it, or
			// asking for a language a brand has nothing in would snap back and
			// look broken.
			if (next.locale !== undefined) setLocale(next.locale);
			if (
				next.topics !== undefined ||
				next.intent !== undefined ||
				next.category !== undefined ||
				next.tag !== undefined ||
				next.locale !== undefined
			) {
				setScoped((was) => {
					const base = was.entityId === entityId ? was : rescope(was, entityId);
					return {
						entityId,
						topics: next.topics ?? base.topics,
						intent: next.intent ?? base.intent,
						category: next.category ?? base.category,
						tag: next.tag ?? base.tag,
						locale: next.locale ?? base.locale,
					};
				});
			}
		},
		[entityId, setRange, setEngine, setLocale],
	);

	// The remembered language is a person's, not a brand's: somebody who read an
	// English brand last week opens a Chinese one and the panel would show them
	// an empty English reading of it. Their own pick on this brand wins; failing
	// that, the language the brand actually has.
	const reading = current.locale || (!available?.length || available.includes(locale) ? locale : available[0]);

	return [
		{
			range,
			engine,
			locale: reading,
			topics: current.topics,
			intent: current.intent,
			category: current.category,
			tag: current.tag,
		},
		set,
	];
}

interface Scoped {
	entityId: string;
	topics: string;
	intent: string;
	category: string;
	tag: string;
	/** The reading language chosen while on this brand, if anybody chose one. */
	locale: string;
}

function rescope(was: Scoped, entityId: string): Scoped {
	return { ...was, entityId, topics: "", tag: "", locale: "" };
}
